type EstablishVerifiedSessionInput = {
	idToken: string;
	userId?: string | null;
};

function decodeJwtSub(idToken: string): string | null {
	try {
		const parts = idToken.split(".");
		if (parts.length !== 3) return null;
		const payload = JSON.parse(
			atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")),
		) as { sub?: string };
		return payload.sub?.trim() || null;
	} catch {
		return null;
	}
}

function normalizeSessionUser(user: Record<string, unknown>, resolvedUserId: string) {
	return {
		...user,
		id: String(user.id ?? user._id ?? resolvedUserId),
	};
}

async function syncNextAuthClientSession(
	idToken: string,
	user: Record<string, unknown>,
): Promise<void> {
	const userId = String(user.id ?? user._id ?? "").trim();
	if (!userId) {
		throw new Error("Account verified but sign-in could not be completed. Please log in.");
	}

	const sessionUser = normalizeSessionUser(user, userId);
	const { signIn, getSession } = await import("next-auth/react");
	const signInResult = await signIn("credentials", {
		redirect: false,
		establishSession: "true",
		idToken,
		user: JSON.stringify(sessionUser),
	});

	if (!signInResult?.ok) {
		throw new Error("Account verified but sign-in could not be completed. Please log in.");
	}

	for (let attempt = 0; attempt < 15; attempt += 1) {
		const session = await getSession();
		if (session?.idToken?.trim() && session.user?.id === userId) {
			return;
		}
		await new Promise((resolve) => setTimeout(resolve, 100));
	}
}

export type EstablishVerifiedSessionResult = {
	user: Record<string, unknown>;
	idToken: string;
};

/** After email OTP verification, create a NextAuth cookie from the signup JWT (no re-login / reCAPTCHA). */
export async function establishVerifiedWebSession({
	idToken,
	userId,
}: EstablishVerifiedSessionInput): Promise<EstablishVerifiedSessionResult> {
	const token = idToken.trim();
	if (!token) {
		throw new Error("Missing signup token");
	}

	const resolvedUserId = userId?.trim() || decodeJwtSub(token);
	if (!resolvedUserId) {
		throw new Error("Could not resolve verified user id");
	}

	const backendUrl = (process.env.NEXT_PUBLIC_BACKEND_URL || "https://api.techxeed.com").replace(
		/\/$/,
		"",
	);

	const userRes = await fetch(`${backendUrl}/users/${resolvedUserId}`, {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
			"X-Requested-With": "XMLHttpRequest",
		},
	});

	if (!userRes.ok) {
		throw new Error("Account verified but session could not be loaded. Please log in.");
	}

	const backendUser = (await userRes.json()) as Record<string, unknown>;
	const sessionUser = normalizeSessionUser(backendUser, resolvedUserId);

	const sessionRes = await fetch("/api/auth/create-session", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({
			idToken: token,
			user: sessionUser,
		}),
	});

	if (!sessionRes.ok) {
		let message = "Account verified but sign-in could not be completed. Please log in.";
		try {
			const data = await sessionRes.json();
			if (typeof data?.error === "string") message = data.error;
		} catch {
			// ignore
		}
		throw new Error(message);
	}

	const sessionData = await sessionRes.json().catch(() => ({}));
	const freshIdToken = String(sessionData?.idToken ?? token).trim();
	const sessionUserFromApi = sessionData?.user as Record<string, unknown> | undefined;
	const resolvedUser = normalizeSessionUser(
		sessionUserFromApi ?? backendUser,
		resolvedUserId,
	);

	await syncNextAuthClientSession(freshIdToken, resolvedUser);

	return {
		user: resolvedUser,
		idToken: freshIdToken,
	};
}
